import React, { ErrorInfo, ReactNode } from "react";
import { ApiError } from "object-models/shared/api-error.model";
import { reportUiError } from "services/ui-error-reporter";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error?: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportUiError(error, {
      feature: "Application",
      action: "render",
      component: "ErrorBoundary",
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
    if (process.env.NODE_ENV === "production") {
      console.error("Unhandled application error", error, errorInfo);
    }
  }

  render() {
    const { error } = this.state;

    if (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : "Something went wrong. Please refresh the page or contact the administrator if the problem persists.";

      return (
        <div
          role="alert"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            backgroundColor: "#f7f7f7",
            color: "#1f1f1f",
            textAlign: "center",
          }}
        >
          <div>
            <h1>Application error</h1>
            <p>{message}</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;